import { computed, onMounted, ref } from 'vue'
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_REGISTRY,
  useFeatureFlag
} from '@gat-ship/feature-flag'
import { useUser } from '@gat-ship/user'
import { selectDefaultDatabaseId } from '../model/default-database'
import {
  featureFlagMeta,
  grantedFeatureFlags,
  isDevInfoVisible,
  isFeatureFlagEnabled as isFeatureFlagEnabledCore,
  nextDisabledFeatureFlags
} from '../model/feature-flag-prefs'

/**
 * Account-panel seam: default-database selection + the dev-info / feature-flag
 * decisions. Reactive reads (`user.*`, `allFlags`, env flags) live here; the
 * pure core decides, and the shell owns the `setItemGlobal` /
 * `updateUserPreferences` writes.
 */
export function useAccountSettings() {
  const user = useUser()
  const { allFlags } = useFeatureFlag()

  const userPreferredDefaultId = ref('')

  onMounted(async () => {
    userPreferredDefaultId.value =
      (await user.userState.global
        .getState('defaultDatabaseId')
        .catch(() => '')) ?? ''
  })

  const effectiveDefaultDatabaseId = computed(() =>
    selectDefaultDatabaseId(
      user.databases.value.all,
      userPreferredDefaultId.value
    )
  )

  async function setDefaultDatabase(databaseId: string) {
    userPreferredDefaultId.value = databaseId
    await user.userState.global.setItemGlobal('defaultDatabaseId', databaseId)
  }

  const showDevInfo = computed(() =>
    isDevInfoVisible(
      { isDev: FEATURE_FLAGS.IS_DEV, isStaging: FEATURE_FLAGS.IS_STAGING },
      user.userProfile.value?.email
    )
  )

  const grantedFlags = computed(() =>
    grantedFeatureFlags(allFlags.value, FEATURE_FLAG_REGISTRY)
  )

  function getFlagMeta(name: string) {
    return featureFlagMeta(name, FEATURE_FLAG_REGISTRY)
  }

  function isFeatureFlagEnabled(name: string) {
    return isFeatureFlagEnabledCore(
      name,
      user.userPreferences.getUserPreferences().disabledFeatureFlags ?? []
    )
  }

  function toggleFeatureFlag(name: string, enabled: boolean) {
    const disabledFeatureFlags = nextDisabledFeatureFlags(
      name,
      enabled,
      user.userPreferences.getUserPreferences().disabledFeatureFlags ?? []
    )
    user.userPreferences.updateUserPreferences({ disabledFeatureFlags })
  }

  return {
    user,
    effectiveDefaultDatabaseId,
    setDefaultDatabase,
    showDevInfo,
    grantedFlags,
    getFlagMeta,
    isFeatureFlagEnabled,
    toggleFeatureFlag
  }
}
